import { FC, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import {
  DraftModel,
  QuickSalePlanModel,
  VariantModel,
} from '../models/QuickSaleModel';
import { getPlanVariants } from '../services/QuickSaleCRUD';
import {
  apiErrorMessage,
  flattenErrors,
  formatEuro,
} from '../utils/quickSaleFormat';
import { PriceBreakdown } from './PriceBreakdown';

type Props = {
  userId: number;
  plan: QuickSalePlanModel;
  onClose: () => void;
  onApply: (draft: DraftModel['plan']) => void;
};

const PERIOD_PREFIX = 'period-';

export function variantLabel(
  intl: ReturnType<typeof useIntl>,
  variant: VariantModel
): string {
  if (variant.key.startsWith(PERIOD_PREFIX)) {
    return intl.formatMessage(
      { id: 'quickSaleVariant.period' },
      { days: variant.key.slice(PERIOD_PREFIX.length) }
    );
  }
  return intl.formatMessage({ id: `quickSaleVariant.${variant.key}` });
}

/** Every price the agent can offer for this plan, simulated by the backend. */
const VariantsModal: FC<Props> = ({ userId, plan, onClose, onApply }) => {
  const intl = useIntl();
  const [variants, setVariants] = useState<VariantModel[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPlanVariants(userId, plan.id)
      .then(({ data }) => {
        if (!cancelled) {
          setVariants(data.variants);
        }
      })
      .catch((error) => {
        toast.error(
          apiErrorMessage(
            error,
            intl.formatMessage({ id: 'quickSale.genericError' })
          )
        );
        if (!cancelled) {
          setVariants([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, plan.id, intl]);

  return (
    <Modal show onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <h4 className="mb-0">
          {intl.formatMessage({ id: 'quickSale.variantsTitle' })}
        </h4>
      </Modal.Header>
      <Modal.Body>
        {variants === null ? (
          <div className="d-flex align-items-center gap-3 text-gray-600">
            <span className="spinner-border spinner-border-sm text-primary" />
            {intl.formatMessage({ id: 'quickSale.calculatingVariants' })}
          </div>
        ) : (
          <table className="table table-row-dashed align-middle gy-3">
            <thead>
              <tr className="fw-bolder text-muted">
                <th>{intl.formatMessage({ id: 'quickSale.variant' })}</th>
                <th>{intl.formatMessage({ id: 'quickSale.gramsPerDay' })}</th>
                <th>{intl.formatMessage({ id: 'quickSale.trial' })}</th>
                <th>{intl.formatMessage({ id: 'quickSale.regularOrder' })}</th>
                <th>{intl.formatMessage({ id: 'quickSale.shipping' })}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <tr key={variant.key}>
                  <td className="fw-bold">
                    {variantLabel(intl, variant)}
                    {variant.result?.custom_lines && (
                      <div className="fs-8 text-muted fw-normal">
                        {variant.result.custom_lines
                          .map(
                            (line) =>
                              `${line.units}x ${line.meal_name} (${line.weight_in_gr} g)`
                          )
                          .join(' + ')}
                      </div>
                    )}
                  </td>
                  {variant.result ? (
                    <>
                      <td>
                        {variant.result.average_grams_per_day
                          ? `${variant.result.average_grams_per_day} g`
                          : '-'}
                      </td>
                      <td>
                        <PriceBreakdown price={variant.result.trial} />
                      </td>
                      <td>
                        <PriceBreakdown price={variant.result.regular} />
                      </td>
                      <td>
                        {formatEuro(intl, variant.result.regular?.shipping)}
                      </td>
                      <td className="text-end">
                        {variant.key !== 'current' && (
                          <Button
                            size="sm"
                            variant="light-primary"
                            onClick={() => onApply(variant.draft)}
                          >
                            {intl.formatMessage({ id: 'quickSale.apply' })}
                          </Button>
                        )}
                      </td>
                    </>
                  ) : (
                    <td colSpan={5} className="text-danger">
                      {flattenErrors(variant.error).join(' · ') ||
                        intl.formatMessage({ id: 'quickSale.genericError' })}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export { VariantsModal };
